import { Stack } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TAPIKeyScope } from '@feathr/blackbox';
import { Checkbox } from '@feathr/components';

import type { IResourceGroup } from './ScopePicker.utils';
import { getTriStateValue } from './ScopePicker.utils';

import * as styles from './ScopePicker.css';

export interface IScopeGroupRowProps {
  group: IResourceGroup;
  selected: Set<TAPIKeyScope>;
  onChange: (next: Set<TAPIKeyScope>) => void;
}

/** Maps a `resource` key to its human-readable label. */
function useResourceLabel(resource: string): string {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    breadcrumbs: t('Breadcrumbs'),
    campaigns: t('Campaigns'),
    custom_fields: t('Custom fields'),
    persons: t('Persons'),
    projects: t('Projects'),
    tags: t('Tags'),
  };
  return labels[resource] ?? resource;
}

/** A resource row: parent checkbox + read/write children (write implies read). */
function ScopeGroupRow({ group, onChange, selected }: Readonly<IScopeGroupRowProps>): JSX.Element {
  const { t } = useTranslation();
  const label = useResourceLabel(group.resource);

  const hasRead = selected.has(group.read);
  const hasWrite = selected.has(group.write);
  const parentValue = getTriStateValue(hasRead && hasWrite, hasRead || hasWrite);

  function handleToggleParent(checked?: boolean): void {
    const next = new Set<TAPIKeyScope>(selected);
    if (checked) {
      next.add(group.read);
      next.add(group.write);
    } else {
      next.delete(group.read);
      next.delete(group.write);
    }
    onChange(next);
  }

  function handleToggleRead(checked?: boolean): void {
    const next = new Set<TAPIKeyScope>(selected);
    // Write implies read, so read stays selected while write is present.
    if (checked || next.has(group.write)) {
      next.add(group.read);
    } else {
      next.delete(group.read);
    }
    onChange(next);
  }

  function handleToggleWrite(checked?: boolean): void {
    const next = new Set<TAPIKeyScope>(selected);
    if (checked) {
      next.add(group.write);
      next.add(group.read);
    } else {
      next.delete(group.write);
    }
    onChange(next);
  }

  return (
    <Stack gap={'xs'}>
      <Checkbox label={label} onChange={handleToggleParent} value={parentValue} />
      <Stack className={styles.children} gap={'xs'}>
        <Checkbox
          disabled={hasWrite}
          label={t('{{resource}} read', { resource: label })}
          onChange={handleToggleRead}
          value={hasRead}
        />
        <Checkbox
          label={t('{{resource}} write', { resource: label })}
          onChange={handleToggleWrite}
          value={hasWrite}
        />
      </Stack>
    </Stack>
  );
}

export default ScopeGroupRow;
